import * as React from 'react';
import { observer } from 'mobx-react';
import { Employee } from 'nomst/Stores/Decision/employee';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { AmountCode, EmployeeCardTabs, FeatureFlags } from 'typings/enums';
import { featureFlagHelper } from 'singletons';
import EmployeeCycleInfo from 'nomst/Components/Decision/EmployeeCardDetails/employeeCycleInfo';
import EmployeeInfoView from 'nomst/Components/Decision/EmployeeCardDetails/employeeInfoView';
import ConversationBadge from 'nomst/Components/Decision/conversationBadge';
import {
    PromotionRequestView
} from 'nomst/Components/Decision/EmployeeCardDetails/PromotionView/promotionRequestView';
import { PayHistoryView } from 'nomst/Components/Decision/EmployeeCardDetails/payHistoryView';
import { ICountryDecisionDispatcher } from 'typings/countryDecision.dispatcher';
import Tab from 'components/Common/tab';
import './employeeCardDetails.scss';
import { User } from 'nomst/Stores/User/user';

export interface IEmployeeCardDetailsProps {
    countryDecisionDispatcher: ICountryDecisionDispatcher;
    employee: Employee;
    configuration: Configuration;
    isCycleLocked: boolean;
    user: User;
    showMultiComponentBonus?: boolean;
    selectedCurrency?: string;
}

export interface IEmployeeCardDetailsState {
    currentTab: EmployeeCardTabs;
    showPayHistory: boolean;
}

export class EmployeeCardDetails extends React.Component<IEmployeeCardDetailsProps, IEmployeeCardDetailsState> {
    constructor(props: IEmployeeCardDetailsProps) {
        super(props);
        this.state = Object.assign({}, this.state, {
            currentTab: EmployeeCardTabs.CycleInfo,
            showPayHistory: featureFlagHelper.featureFlagSettings[FeatureFlags.ShowPayHistory]
        });

        this.handleCloseEmployeeCardDetails = this.handleCloseEmployeeCardDetails.bind(this);
        this.handleChangeTab = this.handleChangeTab.bind(this);
    }

    render(): React.ReactNode {
        return (
            <div className="nomst-employee-card-details">
                {this.renderHeader()}
                {this.renderEmployeeCardTabs()}
                {this.renderEmployeeContent()}
            </div>
        );
    }

    private renderHeader(): React.ReactNode {
        const { employee, countryDecisionDispatcher } = this.props;
        return (
            <div className="nomst-employee-card-details__header">
                <h3 className="nomst-employee-card-details__display-name">{employee.displayName}</h3>
                <ConversationBadge
                    countryDecisionDispatcher={countryDecisionDispatcher}
                    conversationSummary={employee.conversationSummary}
                />
                <button
                    className="nomst-employee-card-details__cancel no-btn-style"
                    onClick={this.handleCloseEmployeeCardDetails}
                >
                    <span className="icon-cancel" />
                </button>
            </div>
        );
    }

    private renderEmployeeCardTabs(): React.ReactNode {
        return (
            <nav className="tabs nomst-employee-card-details__tabs">
                {this.renderEmployeeCardTab('Current Cycle', EmployeeCardTabs.CycleInfo)}
                {this.state.showPayHistory
                    ? this.renderEmployeeCardTab('Pay History', EmployeeCardTabs.PayHistory)
                    : null}
                {this.renderEmployeeCardTab('Employee Details', EmployeeCardTabs.EmployeeInfo)}
                {this.renderEmployeeCardPromotionTab()}
            </nav>
        );
    }

    private renderEmployeeCardPromotionTab(): React.ReactNode {
        const { configuration } = this.props;
        if (configuration.doesBudgetForAmountCodeExist(AmountCode.Promotion)) {
            return this.renderEmployeeCardTab('Promotion Request', EmployeeCardTabs.PromotionRequest);
        }

        return null;
    }

    private renderEmployeeCardTab(tabTitle: string, tab: EmployeeCardTabs): React.ReactNode {
        const isSelected = this.state.currentTab === tab;
        return (
            <Tab
                tabTitle={tabTitle}
                tab={tab}
                isSelected={isSelected}
                onChangeTab={this.handleChangeTab}
            />
        );
    }

    private renderEmployeeContent(): React.ReactNode {
        return (
            <div className="nomst-employee-card-details__display">
                {this.renderEmployeeContentDisplay()}
            </div>
        );
    }

    private renderEmployeeContentDisplay(): React.ReactNode {
        const { currentTab } = this.state;
        const { employee, configuration, countryDecisionDispatcher, isCycleLocked, user } = this.props;
        switch (currentTab) {
            case EmployeeCardTabs.CycleInfo:
                return (
                    <EmployeeCycleInfo
                        configuration={configuration}
                        countryDecisionDispatcher={countryDecisionDispatcher}
                        employee={employee}
                        user={user}
                        isCycleLocked={isCycleLocked}
                        showMultiComponentBonus={this.props.showMultiComponentBonus}
                    />
                );
            case EmployeeCardTabs.PayHistory:
                return (
                    <PayHistoryView
                        payHistory={employee.payHistory}
                        format={configuration.format}
                        selectedCurrency={this.props.selectedCurrency}
                    />
                );
            case EmployeeCardTabs.EmployeeInfo:
                return (
                    <EmployeeInfoView
                        employee={employee}
                    />
                );
            case EmployeeCardTabs.PromotionRequest:
                return (
                    <PromotionRequestView
                        employee={employee}
                        configuration={configuration}
                        countryDecisionDispatcher={countryDecisionDispatcher}
                        isCycleLocked={isCycleLocked}
                        user={user}
                    />
                );
        }
    }

    private handleCloseEmployeeCardDetails(): void {
        const { countryDecisionDispatcher } = this.props;
        countryDecisionDispatcher.dispatchDeselectEmployee();
    }

    private handleChangeTab(tab: EmployeeCardTabs): void {
        this.setState({currentTab: tab});
    }
}

export default observer(EmployeeCardDetails);